import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Building2, MapPin, Phone, Shield, FileSignature, FileText, Landmark,
  RefreshCw, Save, CheckCircle2, AlertCircle, ArrowLeft, Calculator,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TABS = [
  { key: 'identity',    label: 'Identity',         icon: Building2 },
  { key: 'address',     label: 'Address',          icon: MapPin },
  { key: 'contact',     label: 'Contact',          icon: Phone },
  { key: 'zatca',       label: 'ZATCA',            icon: Shield },
  { key: 'certificates',label: 'Certificates',     icon: FileSignature },
  { key: 'invoices',    label: 'Invoice Settings', icon: FileText },
  { key: 'bank',        label: 'Bank',             icon: Landmark },
  { key: 'accounting',  label: 'Accounting',       icon: Calculator },
];

const HealthCard = ({ health }) => {
  if (!health) return null;
  const colour =
    health.score >= 90 ? 'emerald' :
    health.score >= 75 ? 'lime' :
    health.score >= 60 ? 'amber' :
    health.score >= 40 ? 'orange' : 'rose';
  return (
    <div className={`bg-gradient-to-br from-${colour}-50 to-${colour}-100 border-2 border-${colour}-300 rounded-xl p-5`} data-testid="health-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-full bg-${colour}-200`}>
            <CheckCircle2 className={`w-6 h-6 text-${colour}-800`} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Company Health</h3>
            <p className="text-xs text-gray-600">Profile readiness for production / ZATCA</p>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-4xl font-bold text-${colour}-700`} data-testid="health-score">
            {health.score}<span className="text-xl">%</span>
          </div>
          <div className={`text-sm font-semibold text-${colour}-800`}>Grade {health.grade}</div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
        {Object.entries(health.checks).map(([k, ok]) => (
          <div key={k} className={`flex items-center gap-1 ${ok ? 'text-emerald-700' : 'text-rose-700'}`}>
            {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
            <span className="font-mono">{k}</span>
          </div>
        ))}
      </div>
      {health.missing?.length > 0 && (
        <div className="mt-3 text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded p-2">
          <strong>Missing:</strong> {health.missing.map(m => m.label_en).join(' · ')}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', disabled = false, testid, placeholder }) => (
  <div>
    <label className="text-xs text-gray-600 mb-1 block">{label}</label>
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange?.(e.target.value)}
      disabled={disabled}
      data-testid={testid}
      placeholder={placeholder}
      className={`w-full border rounded-lg px-3 py-2 text-sm ${disabled ? 'bg-gray-50 text-gray-500' : 'bg-white'}`}
    />
  </div>
);

const CompanyProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('identity');
  const [profile, setProfile] = useState(null);
  const [health, setHealth] = useState(null);
  const [draft, setDraft] = useState({});
  const [accounts, setAccounts] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [p, h, a] = await Promise.all([
        axiosInstance.get('/company-profile'),
        axiosInstance.get('/company-profile/health'),
        axiosInstance.get('/accounts').catch(() => ({ data: [] })),
      ]);
      setProfile(p.data);
      setHealth(h.data);
      setAccounts(a.data || []);
      setDraft({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load company profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Expense-type accounts eligible for the Administrative-Expense default
  const expenseAccounts = useMemo(() =>
    accounts
      .filter(a => (a.account_type || '').toLowerCase() === 'expenses')
      .filter(a => (a.status || 'active') !== 'inactive')
      .sort((a, b) => (a.account_number || '').localeCompare(b.account_number || '')),
    [accounts],
  );

  const get = (path) => {
    // path like 'address.city' — look in draft first, then profile
    const parts = path.split('.');
    const lookup = (obj) => parts.reduce((a, p) => (a == null ? a : a[p]), obj);
    const fromDraft = lookup(draft);
    return fromDraft !== undefined ? fromDraft : lookup(profile);
  };

  const setDraftField = (path, value) => {
    setDraft((prev) => {
      const next = { ...prev };
      const parts = path.split('.');
      if (parts.length === 1) {
        next[parts[0]] = value;
      } else {
        // For nested sections, copy the whole section from current state
        const sec = parts[0];
        const sub = parts.slice(1).join('.');
        const current = { ...(prev[sec] !== undefined ? prev[sec] : (profile?.[sec] || {})) };
        let cur = current;
        const subParts = sub.split('.');
        for (let i = 0; i < subParts.length - 1; i++) {
          cur[subParts[i]] = { ...(cur[subParts[i]] || {}) };
          cur = cur[subParts[i]];
        }
        cur[subParts[subParts.length - 1]] = value;
        next[sec] = current;
      }
      return next;
    });
  };

  const save = async () => {
    if (Object.keys(draft).length === 0) {
      toast.info('No changes to save');
      return;
    }
    setSaving(true);
    try {
      await axiosInstance.patch('/company-profile', draft);
      toast.success('Company profile saved');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-6xl mx-auto" data-testid="company-profile-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/settings')}
            className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            data-testid="back-to-settings"
          >
            <ArrowLeft className="w-4 h-4" /> Settings
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="px-3 py-2 rounded-lg border-2 border-gray-200 text-sm hover:bg-gray-50 inline-flex items-center gap-1" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={save}
            disabled={saving || Object.keys(draft).length === 0}
            data-testid="save-profile-btn"
            className={`px-4 py-2 rounded-lg font-semibold text-sm text-white inline-flex items-center gap-1 ${
              saving || Object.keys(draft).length === 0
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
            }`}
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving…' : `Save${Object.keys(draft).length ? ` (${Object.keys(draft).length})` : ''}`}
          </button>
        </div>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {profile?.legal_name || 'Company Profile'} · <span className="text-base text-gray-500 font-mono">{profile?.company_code}</span>
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Single source of truth for company identity, ZATCA settings, and invoice configuration.
        </p>
      </div>

      <HealthCard health={health} />

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex flex-wrap gap-1 px-3 pt-3 border-b border-gray-100 bg-gray-50">
          {TABS.map((tk) => {
            const Icon = tk.icon;
            const active = tab === tk.key;
            return (
              <button
                key={tk.key}
                onClick={() => setTab(tk.key)}
                data-testid={`tab-${tk.key}`}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-t-lg text-sm font-medium border-b-2 ${
                  active
                    ? 'bg-white border-indigo-600 text-indigo-700'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4" /> {tk.label}
              </button>
            );
          })}
        </div>

        <div className="p-6">
          {tab === 'identity' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Company Code" value={get('company_code')} disabled testid="f-company-code" />
              <Field label="Industry" value={get('industry')} onChange={(v) => setDraftField('industry', v)} testid="f-industry" />
              <Field label="Legal Name (English)" value={get('legal_name')} onChange={(v) => setDraftField('legal_name', v)} testid="f-legal-name" />
              <Field label="Legal Name (Arabic)" value={get('legal_name_ar')} onChange={(v) => setDraftField('legal_name_ar', v)} testid="f-legal-name-ar" />
              <Field label="Trade Name (English)" value={get('trade_name')} onChange={(v) => setDraftField('trade_name', v)} testid="f-trade-name" />
              <Field label="Trade Name (Arabic)" value={get('trade_name_ar')} onChange={(v) => setDraftField('trade_name_ar', v)} testid="f-trade-name-ar" />
              <Field label="CR Number" value={get('cr_number')} onChange={(v) => setDraftField('cr_number', v)} testid="f-cr" />
              <Field label="VAT Number" value={get('vat_number')} onChange={(v) => setDraftField('vat_number', v)} testid="f-vat" />
            </div>
          )}

          {tab === 'address' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Street" value={get('address.street')} onChange={(v) => setDraftField('address.street', v)} testid="f-street" />
              <Field label="Building Number" value={get('address.building_number')} onChange={(v) => setDraftField('address.building_number', v)} testid="f-building" />
              <Field label="Additional Number" value={get('address.additional_number')} onChange={(v) => setDraftField('address.additional_number', v)} testid="f-additional" />
              <Field label="District" value={get('address.district')} onChange={(v) => setDraftField('address.district', v)} testid="f-district" />
              <Field label="City" value={get('address.city')} onChange={(v) => setDraftField('address.city', v)} testid="f-city" />
              <Field label="Postal Code" value={get('address.postal_code')} onChange={(v) => setDraftField('address.postal_code', v)} testid="f-postal" />
              <Field label="Country Code" value={get('address.country_code')} onChange={(v) => setDraftField('address.country_code', v)} testid="f-country" />
            </div>
          )}

          {tab === 'contact' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Phone" value={get('contact.phone')} onChange={(v) => setDraftField('contact.phone', v)} testid="f-phone" />
              <Field label="Email" value={get('contact.email')} onChange={(v) => setDraftField('contact.email', v)} testid="f-email" />
              <Field label="Website" value={get('contact.website')} onChange={(v) => setDraftField('contact.website', v)} testid="f-website" />
              <Field label="Contact Person" value={get('contact.contact_person')} onChange={(v) => setDraftField('contact.contact_person', v)} testid="f-contact-person" />
            </div>
          )}

          {tab === 'zatca' && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                هذه الحقول للقراءة فقط هنا. لتعديلها، استخدم صفحة{' '}
                <button onClick={() => navigate('/zatca/setup')} className="font-semibold underline">إعداد ZATCA</button>.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="ZATCA VAT Number" value={get('zatca_profile.vat_number')} disabled testid="f-zatca-vat" />
                <Field label="Onboarding Status" value={get('zatca_profile.onboarding_status')} disabled testid="f-zatca-status" />
                <Field label="Onboarding Completed" value={get('zatca_profile.onboarding_completed_at')} disabled testid="f-zatca-completed" />
                <Field label="Serial Number" value={get('zatca_profile.serial_number')} disabled testid="f-zatca-serial" />
                <Field label="Profile Version" value={get('zatca_profile.profile_version')} disabled testid="f-zatca-version" />
                <Field label="Sandbox Mode" value={get('zatca_profile.sandbox_mode') ? 'Yes' : 'No'} disabled testid="f-zatca-sandbox" />
              </div>
              <VATCadencePicker />
            </div>
          )}

          {tab === 'certificates' && (
            <div className="space-y-4" data-testid="certs-tab">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={`rounded-lg border-2 p-4 ${get('certificates.has_compliance_csid') ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {get('certificates.has_compliance_csid') ? <CheckCircle2 className="w-5 h-5 text-emerald-700" /> : <AlertCircle className="w-5 h-5 text-gray-400" />}
                    <h4 className="font-semibold">Compliance CSID</h4>
                  </div>
                  <p className="text-xs text-gray-600">Test environment certificate</p>
                </div>
                <div className={`rounded-lg border-2 p-4 ${get('certificates.has_production_csid') ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {get('certificates.has_production_csid') ? <CheckCircle2 className="w-5 h-5 text-emerald-700" /> : <AlertCircle className="w-5 h-5 text-gray-400" />}
                    <h4 className="font-semibold">Production CSID</h4>
                  </div>
                  <p className="text-xs text-gray-600">Live ZATCA integration</p>
                </div>
                <div className="rounded-lg border-2 border-gray-200 bg-gray-50 p-4">
                  <h4 className="font-semibold text-sm">Last Renewed</h4>
                  <p className="text-xs text-gray-600 mt-1">{get('certificates.last_renewed_at') || '—'}</p>
                </div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded p-3 text-xs font-mono text-gray-600 break-all">
                <strong>CSR:</strong> {get('zatca_profile.csr') || '—'}
              </div>
            </div>
          )}

          {tab === 'invoices' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Invoice Prefix" value={get('invoice_settings.invoice_prefix')} onChange={(v) => setDraftField('invoice_settings.invoice_prefix', v)} testid="f-inv-prefix" />
              <Field label="Credit Note Prefix" value={get('invoice_settings.credit_note_prefix')} onChange={(v) => setDraftField('invoice_settings.credit_note_prefix', v)} testid="f-cn-prefix" />
              <Field label="Debit Note Prefix" value={get('invoice_settings.debit_note_prefix')} onChange={(v) => setDraftField('invoice_settings.debit_note_prefix', v)} testid="f-dn-prefix" />
              <Field label="Quotation Prefix" value={get('invoice_settings.quotation_prefix')} onChange={(v) => setDraftField('invoice_settings.quotation_prefix', v)} testid="f-quo-prefix" />
              <Field label="Default VAT %" type="number" value={get('invoice_settings.default_vat_pct')} onChange={(v) => setDraftField('invoice_settings.default_vat_pct', Number(v))} testid="f-default-vat" />
              <Field label="Default Currency" value={get('invoice_settings.default_currency')} onChange={(v) => setDraftField('invoice_settings.default_currency', v)} testid="f-default-ccy" />
              <div className="md:col-span-2">
                <label className="text-xs text-gray-600 mb-1 block">Invoice Notes (Arabic)</label>
                <textarea value={get('invoice_settings.notes_ar') || ''} onChange={(e) => setDraftField('invoice_settings.notes_ar', e.target.value)} rows="2" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="f-notes-ar" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-gray-600 mb-1 block">Invoice Notes (English)</label>
                <textarea value={get('invoice_settings.notes_en') || ''} onChange={(e) => setDraftField('invoice_settings.notes_en', e.target.value)} rows="2" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="f-notes-en" />
              </div>
            </div>
          )}

          {tab === 'bank' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Bank Name" value={get('bank.bank_name')} onChange={(v) => setDraftField('bank.bank_name', v)} testid="f-bank-name" />
              <Field label="Account Number" value={get('bank.bank_account')} onChange={(v) => setDraftField('bank.bank_account', v)} testid="f-bank-account" />
              <Field label="IBAN" value={get('bank.bank_iban')} onChange={(v) => setDraftField('bank.bank_iban', v)} testid="f-bank-iban" />
            </div>
          )}

          {tab === 'accounting' && (
            <div className="space-y-6">
              <div className="border border-amber-200 bg-amber-50/40 rounded-lg p-4 text-sm text-amber-900">
                <strong className="font-semibold">Administrative-vs-Project routing</strong>
                <p className="mt-1 text-amber-800">
                  When an expense has no project assigned, the system classifies it as an
                  Administrative Expense and routes its debit to a 52xxx (G&amp;A) account
                  instead of project Direct Cost (51xxx). Per-category overrides live in
                  <code className="mx-1 px-1 bg-amber-100 rounded text-xs">Expense Category Mappings</code>;
                  the field below is the catch-all fallback.
                </p>
              </div>

              <div>
                <label className="text-xs text-gray-600 mb-1 block">
                  Default Administrative Expense Account
                  <span className="text-gray-400 ml-1">(used when no per-category admin map exists)</span>
                </label>
                <select
                  data-testid="f-admin-default-account"
                  value={get('accounting.admin_expense_default_account_id') || ''}
                  onChange={(e) => {
                    const id  = e.target.value;
                    const acc = expenseAccounts.find(a => a.id === id);
                    setDraftField('accounting.admin_expense_default_account_id',   id || null);
                    setDraftField('accounting.admin_expense_default_account_name', acc ? acc.account_name : null);
                  }}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="">— No default · falls back to report&apos;s debit account —</option>
                  {expenseAccounts.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.account_number} · {a.account_name}
                    </option>
                  ))}
                </select>
                {get('accounting.admin_expense_default_account_id') && (
                  <p className="mt-2 text-xs text-emerald-700">
                    ✓ Admin-classified expenses without a category-level admin override
                    will debit this account.
                  </p>
                )}
              </div>

              {/* P14-C6 · Project Costing Policy */}
              <div className="pt-5 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-1">Project Costing Policy</h4>
                <p className="text-xs text-gray-500 mb-4">
                  How should administrative overhead (52xxx G&amp;A) flow into
                  project profitability calculations?
                </p>

                {[
                  {
                    key:   'direct_cost_only',
                    title: 'Direct Cost Only (Pure G&A)',
                    desc:  'Administrative expenses stay in 52xxx and are NEVER allocated to projects. Project profit = Revenue − Direct Cost only. The Close-Confidence score will not flag an unallocated 52xxx pool.',
                  },
                  {
                    key:   'absorb_overhead',
                    title: 'Allocate Administrative Overhead',
                    desc:  'Administrative expenses are distributed across projects via the Overhead Simulator. A non-zero 52xxx pool with 0% absorption rate is flagged as a close-confidence risk.',
                  },
                ].map(opt => {
                  const active = (get('accounting.project_costing_policy') || 'direct_cost_only') === opt.key;
                  return (
                    <label
                      key={opt.key}
                      data-testid={`f-costing-policy-${opt.key}`}
                      className={`flex items-start gap-3 p-3 border rounded-lg mb-2 cursor-pointer transition ${
                        active ? 'border-emerald-400 bg-emerald-50/50 ring-1 ring-emerald-200' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="costing_policy"
                        value={opt.key}
                        checked={active}
                        onChange={() => setDraftField('accounting.project_costing_policy', opt.key)}
                        className="mt-1 accent-emerald-600"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{opt.title}</div>
                        <div className="text-xs text-gray-500 mt-1">{opt.desc}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyProfile;

// ─── VAT Filing Cadence Picker ────────────────────────────────────
// Standalone widget so toggling cadence doesn't dirty the rest of the
// CompanyProfile draft. Writes directly to PATCH /api/vat/cadence.
function VATCadencePicker() {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [freq, setFreq] = React.useState('quarterly');
  const [startPeriod, setStartPeriod] = React.useState('');

  React.useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get('/vat/cadence');
        if (!cancel) {
          setFreq(data?.frequency || 'quarterly');
          setStartPeriod(data?.filing_start_period || '');
        }
      } catch { /* keep default */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  const save = async (next) => {
    if (next === freq || saving) return;
    setSaving(true);
    try {
      await axiosInstance.patch('/vat/cadence', { frequency: next });
      setFreq(next);
      toast.success(
        next === 'quarterly'
          ? 'تم التبديل إلى الإقرار الربعي (كل 3 أشهر)'
          : 'تم التبديل إلى الإقرار الشهري'
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل تحديث دورة الإقرار');
    } finally {
      setSaving(false);
    }
  };

  // Generate start-period options matching the active cadence. Last
  // 8 quarters or 12 months — same window as the cadence picker.
  const startOptions = React.useMemo(() => {
    const now = new Date();
    const out = [{ value: '', label: '— بدون قيد (احسب من البداية) —' }];
    if (freq === 'quarterly') {
      let y = now.getFullYear();
      let q = Math.floor(now.getMonth() / 3) + 1;
      for (let i = 0; i < 8; i++) {
        out.push({ value: `${y}-Q${q}`, label: `Q${q} ${y}` });
        q -= 1; if (q === 0) { q = 4; y -= 1; }
      }
    } else {
      let y = now.getFullYear();
      let m = now.getMonth() + 1;
      for (let i = 0; i < 12; i++) {
        const name = `${y}-${String(m).padStart(2, '0')}`;
        out.push({ value: name, label: new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) });
        m -= 1; if (m === 0) { m = 12; y -= 1; }
      }
    }
    return out;
  }, [freq]);

  const saveStart = async (sp) => {
    if (saving) return;
    setSaving(true);
    try {
      await axiosInstance.patch('/vat/cadence', {
        filing_start_period: sp === '' ? null : sp,
      });
      setStartPeriod(sp);
      toast.success(sp ? `الفترات قبل ${sp} لن تظهر كتنبيه` : 'تم إلغاء قيد البداية');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل حفظ تاريخ البداية');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="vat-cadence-picker"
      className="mt-4 rounded-xl border-2 border-indigo-200 bg-indigo-50/50 p-4"
    >
      <div className="flex items-center gap-2 mb-2">
        <FileSignature className="w-4 h-4 text-indigo-700" />
        <h4 className="font-bold text-sm text-indigo-900">
          دورة الإقرار الضريبي (VAT Filing Cadence)
        </h4>
      </div>
      <p className="text-xs text-indigo-800/80 mb-3">
        وفقاً لـ ZATCA: الشركات ذات الإيراد السنوي ≤ 40 مليون ريال تقدّم إقراراً <strong>ربعياً</strong>،
        والأكبر من ذلك <strong>شهرياً</strong>. تُقدَّم خلال 30 يوماً من نهاية الفترة.
      </p>
      <div className="grid grid-cols-2 gap-2">
        {[
          { key: 'quarterly', title: 'ربعي', sub: 'كل 3 أشهر', testid: 'cadence-quarterly' },
          { key: 'monthly',   title: 'شهري', sub: 'كل شهر',     testid: 'cadence-monthly' },
        ].map(opt => {
          const active = freq === opt.key;
          return (
            <button
              key={opt.key}
              data-testid={opt.testid}
              onClick={() => save(opt.key)}
              disabled={loading || saving}
              className={`text-right rounded-lg border-2 p-3 transition-all ${
                active
                  ? 'border-indigo-600 bg-white shadow-sm'
                  : 'border-indigo-200 bg-white/50 hover:bg-white'
              } disabled:opacity-60`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-[10px] uppercase font-bold ${active ? 'text-indigo-700' : 'text-indigo-400'}`}>
                  {active ? '✓ مفعّل' : 'اختيار'}
                </span>
                <span className="font-bold text-sm text-slate-900">{opt.title}</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5 text-right">{opt.sub}</div>
            </button>
          );
        })}
      </div>

      {/* Filing-start cutoff — hides historical periods from the
          deadline reminder card. Use this if you started using the
          system mid-year so prior periods don't appear as overdue. */}
      <div className="mt-4 pt-4 border-t border-indigo-200/60">
        <label className="text-xs font-bold text-indigo-900 block mb-1">
          أول فترة بدأت فيها استخدام النظام
        </label>
        <p className="text-[11px] text-indigo-800/80 mb-2">
          الفترات قبل هذا التاريخ لن تظهر كتنبيه &ldquo;متأخر&rdquo; في Dashboard. اتركها فارغة لتشمل كل الفترات.
        </p>
        <select
          data-testid="filing-start-period"
          value={startPeriod}
          onChange={(e) => saveStart(e.target.value)}
          disabled={loading || saving}
          className="w-full px-3 py-2 rounded-lg border-2 border-indigo-200 bg-white text-sm text-slate-900 focus:border-indigo-500 disabled:opacity-60"
        >
          {startOptions.map(o => (
            <option key={o.value || 'none'} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
